import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../config/api';
import type {
  PublishVideoBody,
  VideoRunAssetRow,
  VideoRunPostRow,
  VideoRunRow,
} from '../types/api';

export function useVideoRuns(limit = 50) {
  return useQuery({
    queryKey: ['video-runs', limit],
    queryFn: () => api<VideoRunRow[]>(`/video/runs?limit=${limit}`),
    staleTime: 30 * 1000,
  });
}

export function useVideoRun(runId: string | null) {
  return useQuery({
    queryKey: ['video-run', runId],
    queryFn: () =>
      api<{
        run: VideoRunRow;
        assets: VideoRunAssetRow[];
        posts?: VideoRunPostRow[];
      }>(`/video/runs/${String(runId)}`),
    enabled: Boolean(runId),
  });
}

/**
 * POST /video/runs/:id/publish — Phase 4 endpoint; the backend will return
 * a clear error until that lands.
 */
export function usePublishVideo(runId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PublishVideoBody) =>
      api<unknown>(`/video/runs/${String(runId)}/publish`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['video-run', runId] });
      void qc.invalidateQueries({ queryKey: ['video-runs'] });
    },
  });
}
